import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Button, 
  Box,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SchoolIcon from '@mui/icons-material/School';
import HomeIcon from '@mui/icons-material/Home';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Paleta profesional
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  dark: '#2d3436'
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleUserMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const handleLogout = () => {
    handleUserMenuClose();
    setMobileMenuOpen(false);
    logout();
  };

  const isActive = (path) => location.pathname === path;

  const navButtonStyles = (path) => ({
    color: 'white',
    fontWeight: isActive(path) ? 700 : 500,
    fontSize: '0.9rem',
    px: 2,
    py: 1,
    borderRadius: 2,
    textTransform: 'none',
    position: 'relative',
    transition: 'all 0.3s ease',
    backgroundColor: isActive(path) ? 'rgba(255,255,255,0.15)' : 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.2)',
      transform: 'translateY(-2px)'
    },
    '&::after': isActive(path) ? {
      content: '""',
      position: 'absolute',
      bottom: 4,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 20,
      height: 3,
      borderRadius: 2,
      backgroundColor: 'white'
    } : {}
  });

  const NavButton = ({ to, icon, label }) => (
    <Button
      component={Link}
      to={to}
      startIcon={icon}
      sx={navButtonStyles(to)}
      onClick={() => setMobileMenuOpen(false)}
    >
      {label}
    </Button>
  );

  // Mobile drawer content
  const mobileDrawerContent = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        background: `linear-gradient(180deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
        color: 'white'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SchoolIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Aula Digital
          </Typography>
        </Box>
        <IconButton onClick={toggleMobileMenu} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* User info */}
      {user && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'rgba(255,255,255,0.2)',
                fontWeight: 700,
                fontSize: '1.2rem'
              }}
            >
              {user.username?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>
                {user.nombre ? user.nombre + ' ' + (user.apellido || '') : user.username || 'Usuario'}
              </Typography>
              <Chip
                label={user.role === 'admin' ? 'Administrador' : 'Usuario'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white'
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* Navigation */}
      <List sx={{ p: 1 }}>
        <ListItem
          button
          component={Link}
          to="/"
          onClick={toggleMobileMenu}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            bgcolor: isActive('/') ? 'rgba(255,255,255,0.15)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText primary="Inicio" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/mis-cursos"
          onClick={toggleMobileMenu}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            bgcolor: isActive('/mis-cursos') ? 'rgba(255,255,255,0.15)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <MenuBookIcon />
          </ListItemIcon>
          <ListItemText primary="Mis Cursos" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItem>

        {user && (
          <ListItem
            button
            component={Link}
            to="/profile"
            onClick={toggleMobileMenu}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              bgcolor: isActive('/profile') ? 'rgba(255,255,255,0.15)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Mi Perfil" primaryTypographyProps={{ fontWeight: 600 }} />
          </ListItem>
        )}

        {user?.role === 'admin' && (
          <ListItem
            button
            component={Link}
            to="/admin-dashboard"
            onClick={toggleMobileMenu}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              bgcolor: isActive('/admin-dashboard') ? 'rgba(255,255,255,0.15)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Administración" primaryTypographyProps={{ fontWeight: 600 }} />
          </ListItem>
        )}
      </List>

      {/* Footer actions */}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 2 }} />
        {user ? (
          <Button
            fullWidth
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              color: 'white',
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            Cerrar sesión
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              component={Link}
              to="/login"
              startIcon={<LoginIcon />}
              onClick={toggleMobileMenu}
              sx={{
                color: 'white',
                fontWeight: 600,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              Entrar
            </Button>
            <Button
              fullWidth
              component={Link}
              to="/register"
              startIcon={<PersonAddIcon />}
              onClick={toggleMobileMenu}
              sx={{
                color: COLORS.gradient1,
                fontWeight: 600,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
              }}
            >
              Registro
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
          boxShadow: `0 4px 20px ${alpha(COLORS.gradient1, 0.3)}`
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              color: 'white',
              mr: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SchoolIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 800,
                  letterSpacing: -0.5,
                  lineHeight: 1.1
                }}
              >
                MR Technical Solution
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  opacity: 0.85,
                  fontWeight: 500
                }}
              >
                Aula Digital Industrial
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, flexGrow: 1 }}>
              <NavButton to="/" icon={<HomeIcon />} label="Inicio" />
              <NavButton to="/mis-cursos" icon={<MenuBookIcon />} label="Cursos" />
              {user?.role === 'admin' && (
                <NavButton to="/admin-dashboard" icon={<AdminPanelSettingsIcon />} label="Administración" />
              )}
            </Box>
          )}

          {/* Spacer for mobile */}
          {isMobile && <Box sx={{ flexGrow: 1 }} />}

          {/* User section - Desktop */}
          {!isMobile && (
            <>
              {user ? (
                <Button
                  onClick={handleUserMenuOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    color: 'white',
                    textTransform: 'none',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'rgba(255,255,255,0.3)',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      mr: 1
                    }}
                  >
                    {user.username?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.2 }}>
                      {user.nombre ? user.nombre + ' ' + (user.apellido || '') : user.username || 'Usuario'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                      {user.role === 'admin' ? 'Administrador' : 'Usuario'}
                    </Typography>
                  </Box>
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    component={Link}
                    to="/login"
                    startIcon={<LoginIcon />}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 2,
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    Iniciar sesión
                  </Button>
                  <Button
                    component={Link}
                    to="/register"
                    startIcon={<PersonAddIcon />}
                    sx={{
                      bgcolor: 'white',
                      color: COLORS.gradient2,
                      fontWeight: 700,
                      textTransform: 'none',
                      px: 2,
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Registrarse
                  </Button>
                </Box>
              )}
            </>
          )}

          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              onClick={toggleMobileMenu}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* User dropdown menu - Desktop */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
              mx: 1,
              my: 0.5,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(COLORS.primary, 0.1),
                transform: 'translateX(4px)'
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem component={Link} to="/profile" onClick={handleUserMenuClose}>
          <ListItemIcon>
            <PersonIcon sx={{ color: COLORS.primary }} />
          </ListItemIcon>
          <ListItemText 
            primary="Mi Perfil" 
            primaryTypographyProps={{ fontWeight: 600 }}
          />
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon sx={{ color: COLORS.error }} />
          </ListItemIcon>
          <ListItemText 
            primary="Cerrar sesión" 
            primaryTypographyProps={{ fontWeight: 600, color: COLORS.error }}
          />
        </MenuItem>
      </Menu>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            boxShadow: '-8px 0 32px rgba(0,0,0,0.2)'
          }
        }}
      >
        {mobileDrawerContent}
      </Drawer>

      {/* Main content */}
      <Box 
        sx={{ 
          minHeight: 'calc(100vh - 80px)',
          backgroundColor: '#f8fffe'
        }}
      >
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {children}
        </Container>
      </Box>
    </>
  );
};

export default Layout;